import { acquireApiToken } from "@/lib/auth/msal";

/**
 * Typed fetch wrapper around the ptrn-super FastAPI.
 *
 * The bearer token is resolved via MSAL on every call. MSAL's silent
 * acquisition is fast (cache hit) so this is a few microseconds per
 * call once warm.
 *
 * On non-2xx responses, surfaces an `ApiError` with HTTP status, the
 * decoded body (when JSON), and a short summary suitable for surfacing
 * in the UI.
 */

const API_BASE = (import.meta.env.VITE_PTRN_API_BASE ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = "ApiError";
  }

  /** True if this looks like an auth / consent issue. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

interface RequestInitJson<TBody = unknown> {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: TBody;
  /** Set false to skip the bearer (used by /healthz, /.well-known/ptrn). */
  authenticated?: boolean;
  signal?: AbortSignal;
}

/**
 * Execute a JSON-shaped HTTP call against the API.
 *
 * Returns the decoded JSON body, typed as `TResponse`. For 204
 * responses returns `undefined as TResponse`.
 */
export async function apiJson<TResponse, TBody = unknown>(
  path: string,
  init: RequestInitJson<TBody> = {},
): Promise<TResponse> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (init.authenticated !== false) {
    const token = await acquireApiToken();
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: init.method ?? "GET",
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    signal: init.signal,
    credentials: "omit",
  });

  if (response.status === 204) {
    return undefined as TResponse;
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      try {
        body = await response.text();
      } catch {
        // give up
      }
    }
    const detail =
      typeof body === "object" && body !== null && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, body, detail);
  }

  return (await response.json()) as TResponse;
}

/** Resolve the absolute API URL for a path; useful for SSE wiring. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}
